import cn from 'classnames';
import * as d3 from 'd3';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, } from 'react-router-dom';
import { decodeSourceName } from '../../utils/decodeURI';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { Tooltip } from 'react-tooltip';

import { Paginator } from '../Paginator/Paginator';
import { Search } from '../Search/Search';
import { TagChip, getTagLevel2Name, getTagProof } from '../TagsTree';
import styles from './SourcesTable.module.scss';
import { ReactComponent as VerifiedTwitter } from '../../pages/SourcePage/assets/verifiedTwitter.svg';
import { platformIconsMapping } from '../../utils/platforms';
import { Loader } from '../Loader/Loader';
import { mapCompromisedEntityTypes, mapBotEntityTypes } from '../../utils/actors';
import { formatNumber, formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import { ReactComponent as EditIcon} from '../../assets/edit-new.svg';
import { ReactComponent as DeleteIcon} from '../../assets/delete-new.svg';

const statusMapping = {
  ACTIVE: "Active",
  PAUSED: "Paused",
  ARCHIVED: "Archived",
}

const sourceStatusClasses = {
  'ACTIVE': styles.active,
  'PAUSED': styles.paused,
  'ARCHIVED': styles.archived,
}

const sortingFieldNames = {
  ID: 'id',
  AUDIENCE: 'audience',
  ACTOR: 'name',
  PLATFORM: 'source_type',
  STATUS: 'status',
  URL: 'url',
  COUNTRY: 'country',
};

export const SourcesTable = ({
  sources = [],
  onSelect,
  renderPrefixRow,
  searchQuery,
  withSearch,
  checkIsSelected,
  withHeader = true,
  headerClassName = '',
  rowClassName = '',
  state,
  onPageChange,
  selectAllCheckbox,
  controls,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentUser] = useCurrentUser();
  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const hideRiskActors = featureFlags?.includes('hide_risk_actors');

  const cutString = (string) => {
    if (string.length > 30) {
      return string.slice(0, 27) + '...'
    } else {
      return string
    }
  }

  let sortingQuery;

  if(state) {
    const sortingQuery = state.sorting?.fieldName
    ? `${state?.sorting?.isAscending ? '' : '-'}${sortingFieldNames[state.sorting?.fieldName]}`
    : null;
  }

  if(!sources) {
    return <Loader/>
  }

  if(sources?.objects.length === 0) {
    return <div className={styles.noData}>{t('There is no data to display')}</div>
  }

  return (
    <>
      <div className="card">
        {withHeader && (
          <div className={cn('card-header', headerClassName)}>
            <span className='title-total'>{t('Total actors:')} {formatNumber(sources.total)}</span>

            {withSearch && <Search className={styles.search} />}
          </div>
        )}
          <table
            className={cn('messages', styles.messages, {
              selectable: Boolean(onSelect),
            })}
          >
            <thead>
              <tr>
                {renderPrefixRow && selectAllCheckbox ? selectAllCheckbox() : renderPrefixRow ? <td></td> : ''}
                <td className={rowClassName}>{t('Actor')}</td>
                <td className={rowClassName}>{t('Audience')}</td>
                <td className={rowClassName}>{t('Origin country')}</td>
                <td className={rowClassName}>{t('Tags')}</td>
                <td className={rowClassName}>{t('Tag proof')}</td>
                {currentUser.is_super_admin || controls || renderPrefixRow ? <td className={styles.controls}></td> : ''}
              </tr>
            </thead>
            <tbody>
              {sources.objects.map((source) => {
                let followers_cnt = source?.audience;
                const tagProofs = (source?.tags || [])
                  .map((tag) => ({ tag, proof: getTagProof(tag) }))
                  .filter(({ proof }) => proof !== null);

                let botType = null;
                if(source?.suspended_by_platform === true) {
                  botType = mapBotEntityTypes(t)['suspended_by_platform']?.title;
                } else if(source?.inauthentic_behavior === true || source?.is_bot === true) {
                  botType = mapBotEntityTypes(t)['inauthentic_behavior']?.title;
                }else if(source?.immature_account === true) {
                  botType = mapBotEntityTypes(t)['immature_account']?.title;
                }
                return (
                  <tr
                    key={source.id}
                    className={
                      checkIsSelected && checkIsSelected(source)
                        ? 'selected'
                        : undefined
                    }
                  >
                    {renderPrefixRow?.(source)}

                    <td className={styles.mainInfo}>
                      <div className={styles.sourceNameWrapper}>
                        <div className={styles.platformWrapper}>
                          {platformIconsMapping[source.source_type]}
                        </div>
                        <div className={styles.sourceNameContainer}>
                          <span>
                            <Link to={`/sources/${source.id}`}>
                              {decodeSourceName(source.name)}
                            </Link>
                            {source?.is_verified ? (
                              <span className={styles.verified}>
                                <VerifiedTwitter />
                              </span>
                            ) : (
                              ''
                            )}
                          </span>
                          <div className={styles.sourceInfo}>
                            <span>#{source.id}</span>
                            <span>&#8226;</span>
                            <a
                              href={source?.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {cutString(decodeSourceName(source?.url))}
                            </a>
                            <span>&#8226;</span>
                            <span
                              className={sourceStatusClasses[source.status]}
                            >
                              {statusMapping[source.status]}
                            </span>
                          </div>
                          <div className={styles.tagsContainer}>
                            {source?.affiliations?.length > 0 ? (
                              <div className={styles.dualTag}>
                                <div className={styles.tagKey}>
                                  {t('State-affiliated')}
                                </div>
                                <div className={styles.tagValue}>
                                  {source?.affiliations[0]?.country?.code}
                                </div>
                              </div>
                            ) : (
                              ''
                            )}
                            {source?.discreditations?.length > 0 && !hideRiskActors ? (
                              <div
                                className={styles.tag}
                                data-tooltip-id="discreditations-tag"
                                data-tooltip-content={
                                  mapCompromisedEntityTypes(t)[
                                    source?.discreditations[0]?.entity_type
                                  ]?.tooltip
                                }
                              >
                                {
                                  mapCompromisedEntityTypes(t)[
                                    source?.discreditations[0]?.entity_type
                                  ]?.title
                                }
                              </div>
                            ) : (
                              ''
                            )}

                            {botType === null ? (
                              ''
                            ) : (
                              <div className={styles.tag}>{botType}</div>
                            )}

                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{formatWithCustomGigaBillion(followers_cnt)}</td>
                    <td>{source?.origin_country?.name}</td>
                    <td className={styles.tagsCell}>
                      <div className={styles.tagsContainer}>
                        {source?.tags?.map((tag) => (
                          <TagChip key={tag.full_path} tag={tag} variant="compact" />
                        ))}
                      </div>
                    </td>
                    <td className={styles.proofsCell}>
                      {tagProofs.length > 0 ? (
                        <ul className={styles.proofsList}>
                          {tagProofs.map(({ tag, proof }) => (
                            <li key={tag.full_path}>
                              {proof.link ? (
                                <a
                                  href={proof.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.proofLink}
                                  data-tooltip-id="tag-proof"
                                  data-tooltip-content={getTagLevel2Name(tag)}
                                >
                                  {proof.title || cutString(decodeSourceName(proof.link))}
                                </a>
                              ) : (
                                <span className={styles.proofText}>
                                  <span className={styles.proofTag}>{getTagLevel2Name(tag)}:</span> {proof.title}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        ''
                      )}
                    </td>
                    {controls ? (
                      <td className={styles.controls}>
                        <div className={styles.controlsContainer}>
                          {currentUser?.is_super_admin ? (
                            <Link
                              to={`/sources/${source.id}/edit`}
                              className="tableActionBtn"
                              data-tooltip-id="edit-source"
                              data-tooltip-content={t('Edit')}
                              aria-label={`${t('Edit')} ${source.name}`}
                            >
                              <EditIcon />
                            </Link>
                          ) : (
                            ''
                          )}
                          {controls && controls(source)}
                        </div>
                      </td>
                    ) : currentUser?.is_super_admin ? (
                      <td className={styles.controls}>
                        <Link
                          to={`/sources/${source.id}/edit`}
                          className="tableActionBtn"
                          data-tooltip-id="edit-source"
                          data-tooltip-content={t('Edit')}
                        >
                          <EditIcon />
                        </Link>
                      </td>
                    ) : (
                      ''
                    )}
                  </tr>
                );
              })}
              <Tooltip id="edit-source" place="bottom"/>
              <Tooltip id="discreditations-tag" place="right"/>
              <Tooltip id="tag-proof" place="bottom"/>
            </tbody>
          </table>
      </div>
      <Paginator
        size={sources.size}
        page={sources.page}
        total={sources.total}
        maxPages={100}  // Temporary fix since we can't go over 10k results in ES, need to use cursor pagination here
        onPageChange={(newPage) => {
          const urlParams = new URLSearchParams();

          if (searchQuery) {
            urlParams.set('q', searchQuery);
          }

          if (state) {
            if (state.searchQuery) {
              urlParams.set('q', searchQuery);
            }

            if (state.platforms && state.platforms.length) {
              state.platforms.forEach((platform) => {
                urlParams.append('source_types', platform);
              });
            }

            if (state.countries.length) {
              state.countries.forEach((country) => {
                urlParams.append('origin_country_ids', country);
              });
            }

            
            urlParams.set('audience_lte', state.audience_lte);
            urlParams.set('audience_gte', state.audience_gte);
            

            if(state.status.length) {
              state.status.forEach((status_) => {
                urlParams.append('statuses', status_);
              });
            }


            if (state?.sorting) {
              if (Array.isArray(state?.sorting?.fieldName)) {
                state?.sorting?.fieldName.forEach((element) => {
                  urlParams.append(
                    'sorting',
                    `${state?.sorting?.isAscending ? '' : '-'}${element}`,
                  );
                });
              } else {
                urlParams.set('sorting', sortingQuery);
              }
            }
            onPageChange(newPage)
          }
          urlParams.set('page', newPage)

          navigate(`?${urlParams.toString()}`);
        }}
      />
    </>
  );
};
